import {Component, OnDestroy, OnInit} from '@angular/core';
import {
  ColDef,
  Column,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  RowClassParams,
  ValueGetterParams,
} from 'ag-grid-community';
import {Store} from '@ngrx/store';
import {takeWhile} from 'rxjs/operators';

import {sItemIntToKey, StockItemUpdateFieldValue, StockItemUpdateObj} from '../../../../shared-utilities/models-old/datastructures';
import {
  selectIsGetStockUpdateChangesLoading,
  selectRetryExcludeFailedBarcodes,
  selectRetryStockUpdateValue,
  selectSelectedNotification,
  selectSelectedStockViewChanges,
} from '../../store/notification.selectors';
import {PropertyToTitle} from '../../../../shared/shared-utils/conversions/properties';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {retryStockUpdate, revertStockUpdate, setRetryStockUpdate} from '../../store/notification.actions';
import {DEFAULT_LOADER_INFINITE} from '../../../../shared/shared-module-ui/models/animations/loading-g.interface';
import {Observable} from 'rxjs';
import {IViewChangesRow} from '../../models/view-changes-row';
import {Ascii} from '../../../../shared/shared-models/global/ascii.enum';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {INotification, INotificationDuplicateBarcodeError} from '../../models';
import {parseDuplicateBarcodeErrorsFromXml} from '../../utils/notification-stock-update-error.utils';
import {prepareStockUpdateRetryPayload} from '../../utils/notification-stock-update-retry.utils';
import {buildStockUpdateRevertPayload, collectStockUpdateFieldKeys} from '../../utils/notification-stock-update-view-changes.utils';

@Component({
  selector: 'app-notification-type-stock-update-view-changes',
  templateUrl: './notification-type-stock-update-view-changes.component.html',
  styleUrls: ['./notification-type-stock-update-view-changes.component.scss'],
  standalone: false,
})
export class NotificationTypeStockUpdateViewChangesComponent implements OnInit, OnDestroy {


  retryStockUpdate = false;
  retryExcludeFailedBarcodes = false;
  duplicateBarcodeErrors: INotificationDuplicateBarcodeError[] = [];
  barcodeRemovalCodes = new Set<string>();
  changes: Record<string, StockItemUpdateObj> = {};
  itemCount = 0;
  columnDefs: ColDef[] = [];
  rowData: IViewChangesRow[] = [];
  collapsedGroups = new Set<string>();
  selectedNodes: IRowNode[] = [];
  showModal = false;
  private isAlive = true;
  gridOptions: GridOptions = {
    rowSelection: {
      mode: 'multiRow',
      checkboxes: true,
      headerCheckbox: true,
      enableClickSelection: false,
      selectAll: 'filtered',
    },
    selectionColumnDef: {
      pinned: 'right',
    },
    suppressRowClickSelection: true,
  };
  iconColourEnum = IconColourEnum;
  defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    minWidth: 100,
    cellClass: 'ag-left-aligned-cell',
    comparator: (valueA: string | number, valueB: string | number, nodeA: IRowNode, nodeB: IRowNode): number => {
      const dataA = nodeA?.data as IViewChangesRow;
      const dataB = nodeB?.data as IViewChangesRow;

      if (dataA?.sortOrder !== dataB?.sortOrder) {
        return dataA.sortOrder - dataB.sortOrder;
      }
      if (dataA?.isParent !== dataB?.isParent) {
        return dataB.isParent ? 1 : -1;
      }
      return valueA?.toString().localeCompare(valueB?.toString()) ?? 0;
    },
  };
  rowClassRules: Record<string, (params: RowClassParams) => boolean> = {};
  hoveredCodes: string = null;
  selectIsGetUpdateChanges$: Observable<boolean>;
  searchText: string;

  protected readonly icons = IconsEnum;
  protected readonly animation = DEFAULT_LOADER_INFINITE;

  private gridApi!: GridApi;
  selectedNotification$: Observable<INotification>;

  constructor(
    private readonly store: Store,
  ) {
  }

  get uniqueSelectedNodes(): IRowNode[] {
    const seen = new Set<string>();
    return this.selectedNodes.filter((node: IRowNode<IViewChangesRow>) => {
      const code: string = node.data.code;
      if (seen.has(code)) return false;
      seen.add(code);
      return true;
    });
  }

  ngOnInit(): void {
    this.selectIsGetUpdateChanges$ = this.store.select(selectIsGetStockUpdateChangesLoading);
    this.selectedNotification$ = this.store.select(selectSelectedNotification);

    this.store.select(selectRetryStockUpdateValue)
      .pipe(takeWhile(() => this.isAlive, true))
      .subscribe((retryStockUpdate: boolean) => {
        this.retryStockUpdate = retryStockUpdate;
      });

    this.store.select(selectRetryExcludeFailedBarcodes)
      .pipe(takeWhile(() => this.isAlive, true))
      .subscribe((excludeFailedBarcodes: boolean) => {
        this.retryExcludeFailedBarcodes = excludeFailedBarcodes;
      });

    this.store.select(selectSelectedNotification)
      .pipe(takeWhile(() => this.isAlive, true))
      .subscribe((notification: INotification) => {
        this.duplicateBarcodeErrors = parseDuplicateBarcodeErrorsFromXml(notification?.response ?? '');
        this.barcodeRemovalCodes = new Set<string>(
          this.duplicateBarcodeErrors.map((error: INotificationDuplicateBarcodeError) => error.stockCode),
        );
      });
  }

  willRemoveBarcodes(stockCode: string): boolean {
    return this.retryExcludeFailedBarcodes && this.barcodeRemovalCodes.has(stockCode);
  }

  getFailedBarcodes(stockCode: string): string[] {
    return this.duplicateBarcodeErrors.find((error: INotificationDuplicateBarcodeError) => error.stockCode === stockCode)
      ?.failedBarcodes ?? [];
  }

  getSelectedChangesForRetry(): Record<string, StockItemUpdateObj> {
    const selectedChanges: Record<string, StockItemUpdateObj> = {};
    this.gridApi?.getSelectedNodes()
      .map((node: IRowNode) => node.data as IViewChangesRow)
      .filter((data: IViewChangesRow) => data.isParent)
      .forEach((parent: IViewChangesRow) => {
        if (parent.code && this.changes[parent.code]) {
          selectedChanges[parent.code] = this.changes[parent.code];
        }
      });
    return selectedChanges;
  }

  hasRetryPayload(): boolean {
    if (!this.retryStockUpdate || !this.gridApi) {
      return false;
    }
    const payload: Record<string, StockItemUpdateObj> = prepareStockUpdateRetryPayload(
      this.getSelectedChangesForRetry(),
      this.duplicateBarcodeErrors,
      this.retryExcludeFailedBarcodes,
    );
    return Object.keys(payload).length > 0;
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;

    this.store.select(selectSelectedStockViewChanges)
      .pipe(takeWhile(() => this.isAlive, true))
      .subscribe((changes: Record<string, StockItemUpdateObj>) => {
        if (!changes || Object.keys(changes).length === 0) return;
        this.changes = changes;

        const codes = Object.keys(this.changes || {});
        this.itemCount = codes.length;
        if (codes.length === 0) return;

        const allKeys = new Set<string>();
        const detailKeys = new Set<string>();

        codes.forEach((code: string) => {
          const change = this.changes[code];
          collectStockUpdateFieldKeys(change).forEach((key: string) => allKeys.add(key));
          Object.keys(change.details || {}).forEach((detail: string) => detailKeys.add(detail));
        });

        const dynamicKeys = Array.from(allKeys);
        const cols: ColDef[] = [];

        cols.push({field: 'sortOrder', hide: true, sortable: true});

        cols.push({
          headerName: 'Code',
          field: 'code',
          width: 120,
          cellRenderer: (params: { data: IViewChangesRow; value: string; }) => {
            const data = params.data;
            if (!data.isParent) return null;
            const div = document.createElement('div');
            div.className = 'ag-cell-span';
            div.innerText = params.value ?? '';
            return div;
          },
        });

        const firstDetailKey = Array.from(detailKeys)[0];
        if (firstDetailKey) {
          cols.push({
            headerName: PropertyToTitle[sItemIntToKey[firstDetailKey]],
            field: `details.${firstDetailKey}`,
            width: 200,
            valueGetter: (params) => (params.data as IViewChangesRow).details?.[firstDetailKey] ?? '',
            getQuickFilterText: params => String(params.value ?? ''),
            cellRenderer: (params: { data: IViewChangesRow; value: string; }) => {
              const data = params.data;
              if (!data.isParent) return null;
              const div = document.createElement('div');
              div.className = 'ag-cell-span';
              div.innerText = params.value ?? '';
              return div;
            },
          });
        }

        cols.push({
          headerName: 'Type',
          field: 'isNew',
          width: 150,
          cellRenderer: (params: { data: IViewChangesRow; }) => {
            const container = document.createElement('div');
            container.classList.add('text-center');

            const data = params.data;

            if (data.isParent) {
              const isCollapsed = this.collapsedGroups.has(data.code);

              const expandButton = document.createElement('span');
              expandButton.className = `expand-button ${!isCollapsed ? 'rotated' : ''}`;
              expandButton.dataset.action = 'toggle';
              container.appendChild(expandButton);

              const badgeNew = document.createElement('span');
              badgeNew.className = 'badge-new';
              badgeNew.innerText = 'New';
              container.appendChild(badgeNew);
            } else {
              const arrow = document.createElement('span');
              arrow.className = 'child-arrow';
              arrow.innerText = Ascii.DownRightArrow;
              container.appendChild(arrow);

              const badgeOld = document.createElement('span');
              badgeOld.className = 'badge-old';
              badgeOld.innerText = 'Old';
              container.appendChild(badgeOld);
            }

            return container;
          },
        });

        dynamicKeys.forEach((key: string) => {
          cols.push({
            headerName: PropertyToTitle[sItemIntToKey[key]],
            field: key,
            getQuickFilterText: params => String(params.value ?? ''),
            valueGetter: (params: ValueGetterParams): string => {
              const data = params.data as IViewChangesRow;
              const raw = data.isNew ? data.n?.[key] : data.o?.[key];
              return this.formatValue(raw);
            },

            comparator: (a, b, nodeA, nodeB) => {
              const parentA = this.getParentForRow(nodeA.data as IViewChangesRow);
              const parentB = this.getParentForRow(nodeB.data as IViewChangesRow);

              const aVal = parentA?.n?.[key] ?? '';
              const bVal = parentB?.n?.[key] ?? '';

              const aStr = aVal?.toString() ?? '';
              const bStr = bVal?.toString() ?? '';

              return aStr.localeCompare(bStr);
            },

            cellRenderer: (params: { data: IViewChangesRow; value: string; }) => {
              const data = params.data;
              const n = this.formatValue(data.n?.[key]);
              const o = this.formatValue(data.o?.[key]);
              const span = document.createElement('span');
              const isChanged = n !== o;

              if (!isChanged) {
                span.innerText = params.value ?? '';
                return span;
              }

              span.className = 'value-changed';
              if (data.isNew && key === String(12)) {
                if (Number(n) > Number(o)) {
                  span.innerText = ` ${n} ⬆`;
                } else if (Number(n) < Number(o)) {
                  span.innerText = `${n} ⬇ `;
                } else if (n) {
                  span.innerText = `${n}`;
                } else {
                  span.innerText = '';
                }
              } else if (data.isNew) {
                if (n) {
                  span.innerText = `${n}`;
                } else {
                  span.innerText = '';
                }
              } else {
                if (o) {
                  span.innerText = `${o}`;
                } else {
                  span.innerText = params.value !== 'undefined' ? params.value : '';
                }
              }
              return span;
            },
          });
        });

        cols.push({
          headerName: '',
          field: 'selected',
          pinned: 'right',
          lockPosition: 'right',
          lockPinned: true,
          suppressMovable: true,
          lockVisible: true,
          maxWidth: 50,
          width: 50,
          minWidth: 50,
          filter: false,
          suppressHeaderMenuButton: true,
          suppressHeaderFilterButton: true,
          sortable: false,
          resizable: false,
        });

        this.columnDefs = cols;
        this.rowData = codes.flatMap((code, index) => [
          {
            code,
            details: this.changes[code].details,
            n: this.changes[code].n,
            o: this.changes[code].o,
            isNew: true,
            isParent: true,
            sortOrder: index,
            visible: true,
          },
          {
            code,
            details: this.changes[code].details,
            n: this.changes[code].n,
            o: this.changes[code].o,
            isNew: false,
            isParent: false,
            sortOrder: index,
            visible: true,
          },
        ]);

        this.rowClassRules = {
          ['group-parent-row']: (params: RowClassParams): boolean => (params.data as IViewChangesRow).isParent,
          ['group-child-row']: (params: RowClassParams): boolean => !(params.data as IViewChangesRow).isParent,
          ['hidden']: (params: RowClassParams): boolean => !(params.data as IViewChangesRow).visible,
          ['pair-top-border']: (params: RowClassParams): boolean => params.node.rowIndex % 2 === 0,
          ['ag-row-hover']: (params: RowClassParams): boolean => this.hoveredCodes === (params.data as IViewChangesRow).code,
          ['barcode-removal-row']: (params: RowClassParams): boolean => {
            const data = params.data as IViewChangesRow;
            return this.willRemoveBarcodes(data.code);
          },
        };

        dynamicKeys.forEach((key: string) => {
          const isColumnEmpty = this.rowData.every((row: IViewChangesRow) => {
            const n = row.n?.[key];
            const o = row.o?.[key];
            return (n === undefined || n === null || n === '' || JSON.stringify(n) === '[]') &&
              (o === undefined || o === null || o === '' || JSON.stringify(o) === '[]');
          });

          if (isColumnEmpty) {
            const col = cols.find((colDef: ColDef) => colDef.field === key);
            if (col) {
              col.hide = true;
            }
          }
        });

        setTimeout(() => {
          const eGridDiv = document.querySelector('.ag-root');
          const gridBody: HTMLElement = eGridDiv?.querySelector('.ag-body-viewport');

          if (!gridBody) return;

          const totalColumnWidth = params.api.getAllDisplayedColumns()
            .map((col: Column) => col.getActualWidth())
            .reduce((a: number, b: number) => a + b, 0);

          if (totalColumnWidth < gridBody.offsetWidth) {
            this.gridApi.sizeColumnsToFit();
          }

          if (this.retryStockUpdate) {
            this.gridApi.selectAll();
            this.selectedNodes = this.gridApi.getSelectedNodes();
          }
        }, 100);
      });
  }

  formatValue(v: string | number | string[] | boolean | Date): string {
    if (v == null) return '';
    if (Array.isArray(v)) {
      return v.join(', ');
    }

    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  }

  onSelectionChanged(event: { node: IRowNode }): void {
    const selectedNode = event.node;
    const currentData = selectedNode.data as IViewChangesRow;
    if (!currentData?.code) return;

    try {
      const siblingNode = this.gridApi.getRenderedNodes().find(
        node => (node.data as IViewChangesRow).code === currentData.code &&
          (node.data as IViewChangesRow).isParent !== currentData.isParent,
      );

      if (siblingNode) {
        siblingNode.setSelected(selectedNode.isSelected());
      }
      this.selectedNodes = this.gridApi.getSelectedNodes();
    } catch (error) {
      console.warn('Selection handling failed, using fallback:', error);
      // Fallback: just track the current node
      this.selectedNodes = selectedNode.isSelected() ? [selectedNode] : [];
    }
  }

  onQuickFilterChanged(searchString: string): void {
    this.searchText = searchString;
    if (this.gridApi) {
      this.gridApi.setGridOption('quickFilterText', searchString);
    }
  }

  getSwappedSelectedChanges(): void {
    const selectedNodes = this.gridApi.getSelectedNodes();
    const selectedCodes = selectedNodes
      .map((node: IRowNode) => node.data as IViewChangesRow)
      .filter((data: IViewChangesRow) => data.isParent)
      .map((parent: IViewChangesRow) => parent.code)
      .filter((code: string): code is string => Boolean(code));

    const swapped: Record<string, StockItemUpdateObj> = buildStockUpdateRevertPayload(this.changes, selectedCodes);

    this.showModal = false;
    this.store.dispatch(setRetryStockUpdate({retryStockUpdate: false, excludeFailedBarcodes: false}));
    this.store.dispatch(revertStockUpdate({stockViewChanges: swapped}));
  }

  retrySelectedChanges(): void {
    const payload: Record<string, StockItemUpdateObj> = prepareStockUpdateRetryPayload(
      this.getSelectedChangesForRetry(),
      this.duplicateBarcodeErrors,
      this.retryExcludeFailedBarcodes,
    );

    if (Object.keys(payload).length === 0) {
      return;
    }

    this.showModal = false;
    this.store.dispatch(setRetryStockUpdate({retryStockUpdate: false, excludeFailedBarcodes: false}));
    this.store.dispatch(retryStockUpdate({stockViewChanges: payload}));
  }

  private getParentForRow(data: IViewChangesRow): IViewChangesRow {
    return this.rowData.find((row: IViewChangesRow) => row.code === data.code && row.isParent) || data;
  }
}
